/**
 * 팝업 관리자 폼 상호작용.
 *
 * - 템플릿 선택 → data-popup-template 갱신(불필요 필드 SCSS 토글) + 미리보기 재렌더
 * - 노출 페이지(page_target) → URL 규칙 필드 표시/숨김
 * - 노출 시점(trigger_type) → 트리거 값 필드 표시/숨김
 * - 라이브 미리보기(PopupPreview) 마운트 + 폼 입력에 rAF 디바운스로 반영
 *
 * AdminLayout.init() 에서 initPopupForm() 1줄로 마운트.
 */
import { PopupPreview } from "./PopupPreview";

interface PopupFormRoot extends HTMLElement {
    _popupFormInited?: boolean;
}

function mount(root: PopupFormRoot): void {
    if (root._popupFormInited) return;
    root._popupFormInited = true;

    const preview = new PopupPreview(root);

    // 템플릿 → data-popup-template
    root.querySelectorAll<HTMLInputElement>('input[name="template"]').forEach((radio) => {
        radio.addEventListener("change", () => {
            if (radio.checked) {
                root.setAttribute("data-popup-template", radio.value);
                preview.refresh();
            }
        });
    });

    // 노출 페이지 조건 → URL 규칙 필드
    const togglePageRules = (): void => {
        const sel = root.querySelector<HTMLInputElement>('input[name="page_target"]:checked');
        const show = (sel?.value || "all") === "selected";
        root.querySelectorAll<HTMLElement>('[data-popup-when="page-selected"]').forEach((el) => {
            el.hidden = !show;
        });
    };
    root.querySelectorAll<HTMLInputElement>('input[name="page_target"]').forEach((radio) => {
        radio.addEventListener("change", togglePageRules);
    });
    togglePageRules();

    // 트리거 타입 → 값 필드
    const triggerType = root.querySelector<HTMLSelectElement>('[data-popup-trigger-type]');
    const triggerValue = root.querySelector<HTMLElement>('[data-popup-trigger-value]');
    const toggleTrigger = (): void => {
        const t = triggerType?.value || "immediate";
        const wrap = triggerValue?.closest(".admin-form__group") || triggerValue;
        const needsValue = t === "delay" || t === "scroll" || t === "element";
        if (triggerValue) triggerValue.style.display = needsValue ? "" : "none";
        void wrap;
    };
    triggerType?.addEventListener("change", toggleTrigger);
    toggleTrigger();

    // 입력 → 미리보기 (rAF 디바운스)
    let scheduled = false;
    const schedule = (): void => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
            scheduled = false;
            preview.refresh();
        });
    };
    root.addEventListener("input", schedule);
    root.addEventListener("change", schedule);
    // 언어 탭 전환 시에도 갱신
    root.querySelectorAll("[data-locale-target]").forEach((btn) => btn.addEventListener("click", () => window.setTimeout(schedule, 0)));

    preview.refresh();
}

export function initPopupForm(scope: Document | HTMLElement = document): void {
    const roots =
        scope instanceof HTMLElement && scope.matches?.("[data-popup-form]")
            ? [scope as PopupFormRoot]
            : Array.from((scope as ParentNode).querySelectorAll<PopupFormRoot>("[data-popup-form]"));
    roots.forEach(mount);
}
